(() => {
  const node = document.getElementById('fs-protection-experiment');
  if (!node) return;
  const config = JSON.parse(node.textContent);
  const isFree = config.arm === 'free';
  const root = window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  let submitting = false;
  let status;
  const properties = { _fs_protection_experiment: config.id, _fs_protection_arm: config.arm };

  function show(message) {
    if (!status) {
      status = document.createElement('p');
      status.className = 'fs-experiment-status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      document.body.append(status);
    }
    status.textContent = message;
    status.hidden = !message;
  }

  async function request(path, body) {
    const response = await fetch(`${root}${path}`, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Your cart could not be updated. Please try again.');
    return data;
  }

  function serialize(work) {
    const operation = queue.catch(() => {}).then(work);
    queue = operation;
    return operation;
  }

  async function reconcile() {
    let cart = await request('cart.js');
    let changed = false;
    if (isFree) {
      // Shopify's Ajax line key can be identical for the same plan under two
      // different parents. Give every child a private parent reference before
      // doing key-based updates, including carts created by older previews.
      const freeIds = new Set(Object.values(config.mapping).map((plan) => Number(plan.freeId)));
      for (let attempt = 0; attempt < cart.items.length; attempt++) {
        const index = cart.items.findIndex((item) => freeIds.has(Number(item.variant_id)) && item.parent_relationship?.parent_key && !item.properties?._fs_parent);
        if (index < 0) break;
        const child = cart.items[index];
        cart = await request('cart/change.js', { line: index + 1, quantity: child.quantity, properties: { ...child.properties, _fs_parent: child.parent_relationship.parent_key } });
        changed = true;
      }
      for (let attempt = 0; attempt < 5; attempt++) {
        const repair = window.FieldSignalExperimentCore.repairs(cart, config.mapping);
        if (Object.keys(repair.updates).length) {
          cart = await request('cart/update.js', { updates: repair.updates });
        } else if (repair.additions.length) {
          await request('cart/add.js', { items: repair.additions.map((item) => ({ ...item, properties: { ...properties, _fs_parent: item.parent_line_key } })) });
          cart = await request('cart.js');
        } else break;
        changed = true;
      }
      const pending = window.FieldSignalExperimentCore.repairs(cart, config.mapping);
      if (Object.keys(pending.updates).length || pending.additions.length) throw new Error('Please refresh your cart to confirm your free protection before ordering.');
    }
    const previous = cart.attributes?.fs_protection_first_arm;
    const attributes = {
      fs_protection_experiment: config.id,
      fs_protection_first_arm: previous || config.arm,
      fs_protection_arm: config.arm,
      fs_protection_mixed: cart.attributes?.fs_protection_mixed === 'true' || (previous && previous !== config.arm) ? 'true' : 'false',
    };
    if (Object.entries(attributes).some(([key, value]) => cart.attributes?.[key] !== value)) {
      await request('cart/update.js', { attributes });
    }
    return changed;
  }

  window.FieldSignalProtectionExperiment = {
    properties,
    prepare: () => serialize(reconcile),
  };

  function checkout(event) {
    if (!isFree) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (submitting) return;
    submitting = true;
    show('Confirming your free 2-year protection…');
    serialize(reconcile).then(() => window.location.assign(`${root}checkout`)).catch((error) => {
      submitting = false;
      show(error.message);
    });
  }

  // Installed before the existing product handlers. Treatment uses one atomic
  // parent + child add, preserving shopper-selected properties such as Color.
  window.addEventListener('submit', (event) => {
    const form = event.target.closest?.('form');
    if (!form || !isFree) return;
    const action = new URL(form.action, window.location.href).pathname;
    if (/\/cart\/add(?:\.js)?$/.test(action)) {
      const data = new FormData(form);
      const id = Number(data.get('id'));
      const plan = config.mapping[id];
      if (!plan) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (submitting) return;
      submitting = true;
      const button = event.submitter || form.querySelector('[type="submit"]');
      if (button) button.disabled = true;
      const quantity = Math.max(1, parseInt(data.get('quantity') || '1', 10) || 1);
      const selectedProperties = { ...properties };
      for (const [key, value] of data.entries()) {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match && typeof value === 'string') selectedProperties[match[1]] = value;
      }
      show('Adding your kit with free 2-year protection…');
      serialize(async () => {
        if (!plan.freeId || plan.freePrice !== 0 || !plan.available) throw new Error('Free protection is temporarily unavailable. Please refresh or contact us before ordering.');
        await reconcile();
        await request('cart/add.js', { items: [
          { id, quantity, properties: selectedProperties },
          { id: Number(plan.freeId), quantity, parent_id: id, properties: { ...properties, _fs_parent: crypto.randomUUID() } },
        ] });
        await reconcile();
        document.dispatchEvent(new CustomEvent('fieldsignal:free_protection_added', { detail: { experiment: config.id, arm: config.arm, productVariantId: id, quantity } }));
        window.location.assign(`${root}${form.matches('[data-fs-ad-product-form]') ? 'checkout' : 'cart'}`);
      }).catch((error) => {
        submitting = false;
        if (button) button.disabled = false;
        show(error.message);
      });
    } else if (/\/cart$/.test(action) && event.submitter?.name === 'checkout') checkout(event);
  }, true);

  window.addEventListener('quantity-selector:update', (event) => {
    if (!isFree) return;
    const row = event.target.closest?.('tr[data-key]');
    if (!row) return;
    event.stopImmediatePropagation();
    if (row.hasAttribute('data-fs-protection-child')) return;
    const quantity = Math.max(1, parseInt(event.detail?.quantity || '1', 10) || 1);
    show('Updating your kit and included protection…');
    serialize(async () => {
      await reconcile();
      const cart = await request('cart.js');
      const updates = { [row.dataset.key]: quantity };
      for (const item of cart.items) {
        if (item.parent_relationship?.parent_key === row.dataset.key) updates[item.key] = quantity;
      }
      await request('cart/update.js', { updates });
      await reconcile();
      window.location.reload();
    }).catch((error) => show(error.message));
  }, true);

  window.addEventListener('click', (event) => {
    if (isFree && event.target.closest?.('[data-fs-free-included]')) {
      event.preventDefault();
      return;
    }
    const target = event.target.closest?.('[name="checkout"], [data-testid="checkout-button"], a[href]');
    if (!target) return;
    if (target.tagName === 'A' && !/\/checkout\/?$/.test(new URL(target.href).pathname)) return;
    checkout(event);
  }, true);

  // Existing carts receive the treatment too. Preserve any already-granted free
  // coverage in control; flag cross-arm carts instead of charging for it later.
  serialize(reconcile).then((changed) => {
    if (changed && !submitting) window.location.reload();
  }).catch((error) => { if (isFree) show(error.message); });
})();
